import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  deleteNotification as deleteNotificationApi,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../services/notification.api";
import { connectSocket, getSocket } from "../config/socket.config";

const PAGE_SIZE = 20;

const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const initialized = useRef(false);

  const fetchNotifications = useCallback(async (p = 1) => {
    try {
      setLoading(true);
      const res = await listNotifications({ page: p, limit: PAGE_SIZE });
      setNotifications((prev) =>
        p === 1 ? res.notifications : [...prev, ...res.notifications],
      );
      // The API reports the unread total for the whole inbox, not just this page.
      setUnreadCount(res.unreadCount);
      setHasMore(p * PAGE_SIZE < (res.pagination?.total || 0));
      setPage(p);
    } catch {
      /* keep whatever is already on screen */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("Token");
    if (!token || initialized.current) return;
    initialized.current = true;

    fetchNotifications(1);
    connectSocket();

    const socket = getSocket();

    const handleNewNotification = (payload) => {
      const incoming = {
        _id: `tmp-${Date.now()}`,
        title: payload.title,
        message: payload.message,
        isRead: false,
        createdAt: payload.receivedAt || new Date().toISOString(),
        data: payload.metadata || payload.data || {},
      };
      setNotifications((prev) => [incoming, ...prev]);
      setUnreadCount((prev) => prev + 1);
      toast.info(payload.title, { autoClose: 4000 });
    };

    socket.on("notification:new", handleNewNotification);

    // Refetch when socket reconnects (in case missed events while disconnected)
    const handleReconnect = () => fetchNotifications(1);
    socket.on("connect", handleReconnect);

    // Refetch when browser tab becomes visible again
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchNotifications(1);
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      socket.off("notification:new", handleNewNotification);
      socket.off("connect", handleReconnect);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      /* leave the badge as it was */
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      /* leave the badge as it was */
    }
  }, []);

  const deleteNotification = useCallback(async (id) => {
    try {
      await deleteNotificationApi(id);
      setNotifications((prev) => {
        const target = prev.find((n) => n._id === id);
        if (target && !target.isRead) {
          setUnreadCount((c) => Math.max(0, c - 1));
        }
        return prev.filter((n) => n._id !== id);
      });
    } catch {
      /* leave the list as it was */
    }
  }, []);

  const fetchMore = useCallback(() => {
    if (!loading && hasMore) fetchNotifications(page + 1);
  }, [loading, hasMore, page, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    hasMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchMore,
    refetch: () => fetchNotifications(1),
  };
};

export default useNotifications;
